import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "gallery-modal-new__content" }
const _hoisted_2 = { class: "gallery-modal-new__slide-content" }
const _hoisted_3 = ["src", "data-order", "alt", "title", "onLoad"]
const _hoisted_4 = {
  key: 0,
  class: "gallery-modal-new__image-placeholder"
}
const _hoisted_5 = {
  key: 1,
  class: "gallery-modal-new__video-wrapper"
}
const _hoisted_6 = ["src", "title"]
const _hoisted_7 = { class: "gallery-modal-new__navigation" }
const _hoisted_8 = ["id"]
const _hoisted_9 = ["id"]
const _hoisted_10 = ["id"]

import BaseModal from '@lmt-rpb/BaseModal/BaseModal.vue';
import { ref, nextTick, watch, reactive, onBeforeUnmount } from 'vue';
import type { GalleryElement } from '@/interfaces/components/galleryElement';
import { v4 as uuidv4 } from 'uuid';
import { register } from 'swiper/element/bundle';

import 'swiper/css';
import 'swiper/css/pagination';
import FaIcon from '@lmt-rpb/Atoms/FaIcon/FaIcon.vue';
import type { SwiperOptions } from 'swiper/types';

interface Props {
	elements: GalleryElement[];
	initialIndex?: number;
	headerTitle?: string;
}

interface SwiperInstance {
	isBeginning: boolean;
	isEnd: boolean;
	activeIndex: number;
	destroy?: (deleteInstance?: boolean, cleanStyles?: boolean) => void;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'GalleryModalNew',
  props: {
    elements: {},
    initialIndex: { default: 0 },
    headerTitle: { default: '' }
  },
  setup(__props: any, { expose: __expose }) {

register();

const props = __props;

const baseModalRef = ref<InstanceType<typeof BaseModal> | null>(null);
const swiperRef = ref<HTMLElement | null>(null);
const activeIndex = ref(props.initialIndex);
const loadedImages = reactive<Record<number, boolean>>({});
const uniqueId = 'id-' + uuidv4();

const swiperSettings: SwiperOptions = {
	slidesPerView: 1,
	centeredSlides: true,
	initialSlide: props.initialIndex,
	speed: 400,
	resistance: true,
	resistanceRatio: 0.85,
	touchRatio: 1,
	threshold: 5,
	preventInteractionOnTransition: true,
	keyboard: {
		enabled: true,
		onlyInViewport: true,
		pageUpDown: false,
	},
	navigation: {
		nextEl: `#${uniqueId}-next`,
		prevEl: `#${uniqueId}-prev`,
		hiddenClass: 'swiper-button-hidden',
		disabledClass: 'swiper-button-disabled',
	},
	pagination: {
		el: `#${uniqueId}-pagination`,
		dynamicBullets: true,
	},
};

const getVideoSrc = (element: GalleryElement) => {
	if (element.type === 'youtube' || element.type === 'ytube') {
		return `https://www.youtube.com/embed/${element.content}?rel=0`;
	}
	if (element.type === 'vimeo') {
		return `https://player.vimeo.com/video/${element.content}`;
	}
	return element.content;
};

const handleSlideChange = (swiperEvent: CustomEvent) => {
	if (!swiperEvent?.detail[0]) return;

	const swiperInstance = swiperEvent.detail[0];
	activeIndex.value = swiperInstance.activeIndex;

	updateNavigationVisibility(swiperInstance);
};

const updateNavigationVisibility = (swiper: SwiperInstance) => {
	const prevEl = swiperRef.value?.querySelector('.gallery-modal-new__prev');
	const nextEl = swiperRef.value?.querySelector('.gallery-modal-new__next');

	if (prevEl) {
		if (swiper.isBeginning) {
			prevEl.classList.add('swiper-button-hidden');
		} else {
			prevEl.classList.remove('swiper-button-hidden');
		}
	}

	if (nextEl) {
		if (swiper.isEnd) {
			nextEl.classList.add('swiper-button-hidden');
		} else {
			nextEl.classList.remove('swiper-button-hidden');
		}
	}
};

const openModal = () => {
	if (baseModalRef.value) {
		baseModalRef.value.openModal();

		nextTick(() => {
			if (swiperRef.value) {
				const swiperEl = swiperRef.value.querySelector('swiper-container');
				if (swiperEl && swiperEl.swiper) {
					swiperEl.swiper.update();
					swiperEl.swiper.slideTo(props.initialIndex, 0);
					updateNavigationVisibility(swiperEl.swiper);
				}
			}
		});
	}
};

const clearSwiper = () => {
	if (swiperRef.value) {
		const swiperEl = swiperRef.value.querySelector('swiper-container');
		if (swiperEl) {
			swiperEl.swiper?.destroy?.(true, true);
		}
	}
};

onBeforeUnmount(() => {
	clearSwiper();
});

const initializeLoadingStates = () => {
	nextTick(() => {
		const currentIndex = activeIndex.value;

		prepareImageLoading(currentIndex); // Current slide
		prepareImageLoading(currentIndex + 1); // Next slide
		prepareImageLoading(currentIndex - 1); // Previous slide
	});
};

const initializeAdjacentLoadingStates = (currentIndex: number) => {
	// Preload next 2 slides
	prepareImageLoading(currentIndex + 1);
	prepareImageLoading(currentIndex + 2);

	// Preload previous 2 slides
	prepareImageLoading(currentIndex - 1);
	prepareImageLoading(currentIndex - 2);
};

const prepareImageLoading = (index: number) => {
	if (index >= 0 && index < props.elements.length && !loadedImages[index]) {
		loadedImages[index] = false;
	}
};

const handleImageLoaded = (event: Event, index: number) => {
	if (event.target) {
		loadedImages[index] = true;
	}
};

const getLandscapePortraitClass = (element: GalleryElement) => {
	// Default to landscape for non-image types
	if (element.type !== 'image') {
		return 'gallery-modal-new__image-container--landscape';
	}

	const img = document.querySelector(`img[src="${element.content}"]`) as HTMLImageElement;
	if (img && img.naturalWidth && img.naturalHeight) {
		const ratio = img.naturalWidth / img.naturalHeight;
		if (ratio < 0.95) {
			return 'gallery-modal-new__image-container--portrait';
		}
		if (ratio >= 0.95 && ratio <= 1.05) {
			return 'gallery-modal-new__image-container--square';
		}
	}
	return 'gallery-modal-new__image-container--landscape';
};

watch(activeIndex, async (newIndex, oldIndex) => {
	await nextTick(() => {
		handleStopVideo(oldIndex);
		initializeAdjacentLoadingStates(newIndex);
	});
});

const formatCopyrightString = (text?: string) => {
	if (!text) {
		return `© GIATA GmbH 1996 - ${new Date().getFullYear()}`;
	}
	if (text.includes('©')) {
		return text;
	}
	if (text.includes('&copy;')) {
		return text.replace('&copy;', '©');
	}
	return `© ${text}`;
};

nextTick(() => {
	initializeLoadingStates();
});

function handleStopVideo(oldIndex?: number) {
	const slideIndex = oldIndex ?? activeIndex.value;
	const element = props.elements[slideIndex];
	const slideElement = swiperRef.value?.querySelector(`.swiper-slide:nth-child(${slideIndex + 1})`);
	if (slideElement) {
		const videoEmbed = slideElement.querySelector('.gallery-modal-new__video') as HTMLEmbedElement;
		if (videoEmbed) {
			videoEmbed.src = getVideoSrc(element);
		}
	}
}

__expose({ openModal });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseModal, {
    ref_key: "baseModalRef",
    ref: baseModalRef,
    "header-title": _ctx.headerTitle,
    "full-screen": true,
    "without-padding": true,
    class: "gallery-modal-new",
    onClose: handleStopVideo
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", {
          ref_key: "swiperRef",
          ref: swiperRef,
          class: "gallery-modal-new__swiper-container"
        }, [
          _createElementVNode("swiper-container", _mergeProps(swiperSettings, {
            class: "gallery-modal-new__swiper",
            onSwiperslidechange: handleSlideChange
          }), [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.elements, (element, index) => {
              return (_openBlock(), _createElementBlock("swiper-slide", {
                key: index,
                class: "swiper-slide"
              }, [
                _createElementVNode("div", _hoisted_2, [
                  (element.type === 'image')
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: _normalizeClass(["gallery-modal-new__image-container", [
										getLandscapePortraitClass(element),
										{ 'gallery-modal-new__image-container--loading': !loadedImages[index] },
									]])
                      }, [
                        _createElementVNode("img", {
                          class: _normalizeClass(['gallery-modal-new__image', { 'gallery-modal-new__image--loaded': loadedImages[index] }]),
                          src: element.content,
                          "data-order": index,
                          alt: element.alt || `Image ${index + 1}`,
                          title: formatCopyrightString(element?.copyright),
                          onLoad: (e) => handleImageLoaded(e, index)
                        }, null, 42 /* CLASS, PROPS, NEED_HYDRATION */, _hoisted_3),
                        (!loadedImages[index])
                          ? (_openBlock(), _createElementBlock("div", _hoisted_4))
                          : _createCommentVNode("v-if", true)
                      ], 2 /* CLASS */))
                    : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                        _createElementVNode("embed", {
                          class: "gallery-modal-new__video",
                          src: getVideoSrc(element),
                          tabindex: "-1",
                          title: `Video ${index + 1}`
                        }, null, 8 /* PROPS */, _hoisted_6)
                      ]))
                ])
              ]))
            }), 128 /* KEYED_FRAGMENT */))
          ], 16 /* FULL_PROPS */),
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("div", {
              id: `${uniqueId}-prev`,
              class: "gallery-modal-new__prev"
            }, [
              _createVNode(FaIcon, { icon: ['fa-solid', 'fa-chevron-left'] })
            ], 8 /* PROPS */, _hoisted_8),
            _createElementVNode("div", {
              id: `${uniqueId}-next`,
              class: "gallery-modal-new__next"
            }, [
              _createVNode(FaIcon, { icon: ['fa-solid', 'fa-chevron-right'] })
            ], 8 /* PROPS */, _hoisted_9)
          ]),
          _createElementVNode("div", {
            id: `${uniqueId}-pagination`,
            class: "swiper-pagination"
          }, null, 8 /* PROPS */, _hoisted_10)
        ], 512 /* NEED_PATCH */)
      ])
    ]),
    _: 1 /* STABLE */
  }, 8 /* PROPS */, ["header-title"]))
}
}

})